import { createSlice, type PayloadAction, createAction } from '@reduxjs/toolkit';

import type {
  MandatoryKeys,
  GenericPayloadType,
  PlayerHistoryDay,
  PlayerHistoryRound,
  PlayerHistoryRoundDetails,
} from '@ezugi/types';

import { type GameIdToSlugMapKeys } from '../../constants/game';

type RoundDetailsSuccessPayload = GenericPayloadType;
type RoundDetailsFailurePayload = GenericPayloadType;

export type PlayerHistoryRoundWithGameId = MandatoryKeys<PlayerHistoryRound, 'gameId'>

export type PlayerHistoryPayoutsState = Partial<Record<GameIdToSlugMapKeys, number[]>>

export type PlayerHistoryState = {
  roundDetails: Record<string, PlayerHistoryRoundDetails>;
  days: Record<string, PlayerHistoryDay>;
  rounds?: Record<string, PlayerHistoryRound[]>;
  payouts: PlayerHistoryPayoutsState;
  loading: boolean;
  error: boolean;
  played?: number;
  lastRequestTime?: number;
  totalBet?: string;
  totalWin?: string;
  expandedDay?: string;
}

type RequestRoundsPayload = {
  loadMore?: boolean;
  day: string;
}

const initialState : PlayerHistoryState = {
  roundDetails: {},
  days: {},
  payouts: {},
  loading: true,
  error: false,
};

export const roundDetailsRequestAction = createAction<PlayerHistoryRoundWithGameId>('roundDetails/request');
export const roundDetailsSuccessAction = createAction<RoundDetailsSuccessPayload>('roundDetails/success');
export const roundDetailsFailureAction = createAction<RoundDetailsFailurePayload>('roundDetails/failure');

const playerHistorySlice = createSlice({
  name: 'playerHistory',
  initialState,
  reducers: {
    request(state) {
      return {
        ...state,
        loading: true,
        error: false,
      };
    },
    requestRounds(_state, _action: PayloadAction<RequestRoundsPayload>) {},
    failure(state) {
      return {
        ...state,
        loading: false,
        error: true,
      };
    },
    success(state, { payload }) {
      return {
        ...state,
        roundDetails: { ...state.roundDetails },
        ...payload,
        loading: false,
      };
    },
    set() {},
    setPayouts(state, { payload }: PayloadAction<PlayerHistoryPayoutsState>) {
      state.payouts = {
        ...state.payouts,
        ...payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(roundDetailsSuccessAction, (state, { payload }: PayloadAction<RoundDetailsSuccessPayload>) => ({
      ...state,
      roundDetails: {
        ...state.roundDetails,
        ...payload,
      },
    }));
    builder.addCase(roundDetailsFailureAction, (state, { payload }: PayloadAction<RoundDetailsFailurePayload>) => ({
      ...state,
      roundDetails: {
        ...state.roundDetails,
        ...payload,
      },
    }));
  },
});

export const playerHistoryActions = playerHistorySlice.actions;
export default playerHistorySlice.reducer;
